import { useEffect } from "react";
import { useRouteError } from "react-router-dom";
import * as Sentry from "@sentry/react";

const RELOAD_KEY = 'chunk-error-reload-count';
const MAX_RELOADS = 3;

export default function ErrorPage() {
  const error: any = useRouteError();

  useEffect(() => {
    console.error(error);

    // Détecter les erreurs de chargement de chunks (après déploiement)
    const isChunkError =
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('is not a valid JavaScript MIME type') ||
      error?.message?.includes('Failed to load module script');

    if (isChunkError) {
      const count = Number(sessionStorage.getItem(RELOAD_KEY) || '0');

      if (count < MAX_RELOADS) {
        // Incrémenter le compteur et recharger
        sessionStorage.setItem(RELOAD_KEY, String(count + 1));
        console.log(`Chunk loading error detected, reloading... (attempt ${count + 1}/${MAX_RELOADS})`);

        // Petit délai pour éviter un reload trop brutal
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        // Après MAX_RELOADS tentatives, réinitialiser et afficher l'erreur
        sessionStorage.removeItem(RELOAD_KEY);
        // Capturer dans Sentry quand même après plusieurs tentatives
        Sentry.captureException(error, {
          tags: {
            errorType: 'chunk-load-error-max-retries',
          },
        });
      }
    } else {
      // Pour les autres erreurs (non-chunk), capturer explicitement dans Sentry
      // car React Router les considère comme "handled" avec errorElement
      Sentry.captureException(error, {
        tags: {
          errorType: 'react-router-error',
        },
      });
      sessionStorage.removeItem(RELOAD_KEY);
    }
  }, [error]);

  // Vérifier si on a atteint la limite de reloads
  const reloadLimitReached = Number(sessionStorage.getItem(RELOAD_KEY) || '0') >= MAX_RELOADS;

  return (
    <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-p-4">
      <div className="tw-max-w-md tw-w-full tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8">
        <div className="tw-text-center">
          {/* Icône d'erreur */}
          <div className="tw-mx-auto tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 tw-rounded-full tw-bg-red-100 tw-mb-4">
            <svg
              className="tw-h-6 tw-w-6 tw-text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>

          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">
            Une erreur inattendue s'est produite
          </h1>

          <div className="tw-text-gray-600 tw-mb-6 tw-space-y-2">
            {reloadLimitReached ? (
              <p>Une mise à jour de l'application est disponible. Veuillez recharger la page manuellement.</p>
            ) : (
              <>
                <p>L'erreur a été signalée à notre équipe.</p>
                <p>Si vous êtes bloqué, vous pouvez prendre contact avec notre équipe au <strong className="tw-text-gray-900">0 800 944 665</strong>.</p>
              </>
            )}
          </div>

          {/* Boutons d'action */}
          <div className="tw-space-y-3">
            <button
              onClick={() => window.location.href = '/'}
              className="tw-w-full tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-font-medium tw-rounded-md hover:tw-bg-blue-700 tw-transition-colors"
            >
              Retour à l'accueil
            </button>

            <button
              onClick={() => window.location.reload()}
              className="tw-w-full tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-md hover:tw-bg-gray-300 tw-transition-colors"
            >
              Recharger la page
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}